import environment from "@app/config/environment";
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from "@app/queries/apiKeys";
import type { APIKeyCreateResponse } from "@globals";
import Icon from "@lib/common/components/Icon/Icon";
import ModalHeader from "@lib/common/components/modals/ModalHeader";
import { confirm } from "@lib/common/components/modals/modals";
import { formatShortDate } from "@lib/common/utils/formatShortDate";
import { notifications } from "@lib/notification/notification";
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  CopyButton,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";

type CreateKeyFormData = {
  name: string;
};

export default function APIKeys() {
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure(false);
  const [keyModalOpened, { open: openKeyModal, close: closeKeyModal }] = useDisclosure(false);
  const [createdKey, setCreatedKey] = useState<APIKeyCreateResponse | null>(null);

  const { data: apiKeysData, isLoading } = useApiKeys();
  const createApiKeyMutation = useCreateApiKey();
  const revokeApiKeyMutation = useRevokeApiKey();

  const createForm = useForm<CreateKeyFormData>({
    initialValues: {
      name: ""
    },
    validate: {
      name: value => (value.trim().length < 1 ? "Name is required" : null)
    }
  });

  const handleCreateKey = async (values: CreateKeyFormData) => {
    try {
      const result = await createApiKeyMutation.mutateAsync({
        body: {
          name: values.name.trim()
        }
      });

      setCreatedKey(result);

      createForm.reset();
      closeCreateModal();
      openKeyModal();
    } catch {
      notifications.error("Failed to create API key");
    }
  };

  const handleRevokeKey = (apiKeyId: string, keyName: string) => {
    confirm({
      title: "Revoke API Key",
      children: (
        <Text>
          Are you sure you want to revoke the API key &quot;{keyName}&quot;? This action cannot be undone and any
          applications using this key will lose access.
        </Text>
      ),
      labels: { confirm: "Revoke", cancel: "Cancel" },
      danger: true,
      onConfirm: async () => {
        try {
          await revokeApiKeyMutation.mutateAsync({
            params: { path: { api_key_id: apiKeyId } }
          });
        } catch {
          notifications.error("Failed to revoke API key");
        }
      }
    });
  };

  const handleCloseKeyModal = () => {
    setCreatedKey(null);
    closeKeyModal();
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Group gap="xs">
          <Text fw={500} size="lg">
            API Keys
          </Text>
          <Tooltip label="API Documentation" withArrow>
            <Anchor href={`${environment.ATI_API_BASE_URL}/swagger/`} target="_blank">
              <ActionIcon variant="subtle" color="gray" size="sm">
                <Icon name="book" size="sm" />
              </ActionIcon>
            </Anchor>
          </Tooltip>
        </Group>
        <Button
          onClick={openCreateModal}
          leftSection={<Icon name="plus" />}
          size="sm"
          color="secondary"
          variant="light"
        >
          Create API Key
        </Button>
      </Group>

      <Card withBorder>
        <Table highlightOnHover>
          <Table.Tbody>
            {isLoading ? (
              <Table.Tr>
                <Table.Td colSpan={2}>
                  <Text ta="center">Loading...</Text>
                </Table.Td>
              </Table.Tr>
            ) : apiKeysData?.results?.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={2}>
                  <Text ta="center" c="dimmed">
                    No API keys found. Create one to get started.
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              apiKeysData?.results?.map(apiKey => (
                <Table.Tr key={apiKey.id}>
                  <Table.Td w="100%">
                    <Tooltip
                      label={
                        <>
                          <div>
                            <strong>Prefix:</strong> {apiKey.prefix}...
                          </div>
                          <div>
                            <strong>Created:</strong> {formatShortDate(apiKey.created_at)}
                          </div>
                          <div>
                            <strong>Last used:</strong> {formatShortDate(apiKey.last_used_at)}
                          </div>
                        </>
                      }
                      position="bottom-start"
                    >
                      <Group gap="xs">
                        <Text truncate maw={300}>
                          {apiKey.name}
                        </Text>
                        {apiKey.revoked && (
                          <Badge color="red" size="xs">
                            Revoked
                          </Badge>
                        )}
                      </Group>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td align="right">
                    {!apiKey.revoked && (
                      <Tooltip label="Revoke key">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => handleRevokeKey(apiKey.id, apiKey.name)}
                          loading={revokeApiKeyMutation.isPending}
                        >
                          <Icon name="trash" />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Card>

      {/* Create API Key Modal */}
      <Modal opened={createModalOpened} onClose={closeCreateModal} withCloseButton={false} size="md">
        <ModalHeader title={"Create New API Key"} onClose={closeCreateModal} />
        <Box component="form" onSubmit={createForm.onSubmit(handleCreateKey)} p="md">
          <Stack>
            <TextInput
              label="Key Name"
              placeholder="e.g., Production API Key"
              required
              {...createForm.getInputProps("name")}
            />
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeCreateModal}>
                Cancel
              </Button>
              <Button type="submit" loading={createApiKeyMutation.isPending}>
                Create Key
              </Button>
            </Group>
          </Stack>
        </Box>
      </Modal>

      {/* Show Created Key Modal */}
      <Modal
        onClose={handleCloseKeyModal}
        opened={keyModalOpened}
        size="lg"
        closeOnClickOutside={false}
        withCloseButton={false}
      >
        <ModalHeader title={"API Key Created"} onClose={handleCloseKeyModal} />
        <Stack p="md">
          <Alert color="yellow" title="Important">
            Make sure to copy your API key now. You will not be able to see it again!
          </Alert>

          <Text fw={500}>Key Name: {createdKey?.name}</Text>

          <Box>
            <Text size="sm" c="dimmed" mb="xs">
              Your API Key:
            </Text>
            <Group gap="xs">
              <TextInput value={createdKey?.key ?? ""} readOnly ff="monospace" style={{ flex: 1 }} />
              <CopyButton value={createdKey?.key ?? ""}>
                {({ copied, copy }) => (
                  <Button color={copied ? "green" : "blue"} onClick={copy}>
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                )}
              </CopyButton>
            </Group>
          </Box>

          <Text size="sm" c="dimmed">
            Use this key in the Authorization header: <code>Api-Key {createdKey?.prefix}...</code>
          </Text>

          <Group justify="flex-end" mt="md">
            <Button onClick={handleCloseKeyModal}>Done</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
